import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useReportActiveTab, useVisibleReportTabs } from "../state/ReportsV2Context";
import { buildReportPdf } from "./buildReportPdf";
import { forceMountDeferred } from "./forceMountDeferred";
import { PDF_HIDE_ATTR, REPORT_SHARE_ROOT_ATTR, V2_CARD_ATTR } from "./shareScopeContext";
import ShareMenu, { type PreparePdf } from "./ShareMenu";
import { useCanShareReports, useReportPreparedBy } from "./useCanShareReports";
import { useReportFilterSummary } from "./useReportFilterSummary";

/**
 * "Share" beside Export in the Reports top bar: the WHOLE tab on screen —
 * KPI strip, every card, every tile row — as one PDF with the filter bar's
 * settings on top. Cards still waiting below the fold are mounted first and
 * their data awaited, so nothing comes out as a blank placeholder.
 *
 * Renders in the app header (outside ReportsV2), like ReportHeaderTitle, so
 * it reads the same Redux filter state directly. Hidden while a custom report
 * is open — that page has its own Share.
 */

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Wait until react-query has been idle for a moment (bounded). */
async function waitForData(qc: QueryClient): Promise<boolean> {
  const started = Date.now();
  let sawFetching = false;
  let quietSince = 0;
  while (Date.now() - started < 30_000) {
    await sleep(150);
    if (qc.isFetching() > 0) {
      sawFetching = true;
      quietSince = 0;
      continue;
    }
    quietSince ||= Date.now();
    if (Date.now() - quietSince >= 600) break;
  }
  return sawFetching;
}

const isCard = (el: Element) => el.hasAttribute(V2_CARD_ATTR);

/** Element children that paint, looking through display:contents wrappers. */
function visibleChildren(el: Element): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (const child of Array.from(el.children)) {
    if (!(child instanceof HTMLElement) || child.hasAttribute(PDF_HIDE_ATTR)) continue;
    if (getComputedStyle(child).display === "contents") {
      out.push(...visibleChildren(child));
      continue;
    }
    if (child.getBoundingClientRect().height >= 1) out.push(child);
  }
  return out;
}

const stackedVertically = (els: HTMLElement[]) => {
  const rects = els.map((e) => e.getBoundingClientRect()).sort((a, b) => a.top - b.top);
  return rects.every((r, i) => i === 0 || r.top >= rects[i - 1]!.bottom - 1);
};

/**
 * Split the tab into page-break-friendly blocks: walk down through plain
 * vertical stacks until we reach cards or side-by-side rows (a grid row of
 * two cards stays ONE block, so it keeps its layout in the PDF).
 */
function flattenBlocks(el: HTMLElement, depth = 0): HTMLElement[] {
  if (depth > 8 || isCard(el)) return [el];
  const kids = visibleChildren(el);
  if (kids.length === 0 || !stackedVertically(kids)) return [el];
  if (kids.length === 1) return flattenBlocks(kids[0]!, depth + 1);
  if (el.getBoundingClientRect().height < 480) return [el];
  return kids.flatMap((k) => flattenBlocks(k, depth + 1));
}

function collectViewBlocks(root: Element): HTMLElement[] {
  const blocks: HTMLElement[] = [];
  const strip = root.querySelector<HTMLElement>('[data-pdf-block="metrics"]');
  if (strip) blocks.push(strip);
  const panel = Array.from(root.querySelectorAll<HTMLElement>('[role="tabpanel"]')).find(
    (el) => !el.parentElement?.closest('[role="tabpanel"]'),
  );
  if (panel) blocks.push(...flattenBlocks(panel));
  return blocks;
}

/** The tab's own sub-tab pick ("Activity", "Heatmap"…) — not a card's toggle. */
function activeSubTabLabel(root: Element): string | null {
  for (const radio of Array.from(root.querySelectorAll('[role="tabpanel"] [role="radio"][aria-checked="true"]'))) {
    if (!radio.closest(`[${V2_CARD_ATTR}]`)) return radio.textContent?.trim() || null;
  }
  return null;
}

export default function ReportViewShareButton() {
  const [searchParams] = useSearchParams();
  const customOpen = Boolean(searchParams.get("custom"));
  const canShare = useCanShareReports();
  const summary = useReportFilterSummary();
  const preparedBy = useReportPreparedBy();
  const activeTab = useReportActiveTab();
  // The visible set, not REPORT_TABS: it carries the "By <Team>" key-field tabs.
  const visibleTabs = useVisibleReportTabs();
  const tabLabel = visibleTabs.find((t) => t.key === activeTab)?.label ?? "Reports";
  const queryClient = useQueryClient();
  const latest = useRef({ summary, preparedBy, tabLabel });
  useEffect(() => {
    latest.current = { summary, preparedBy, tabLabel };
  }, [summary, preparedBy, tabLabel]);

  const prepare = useCallback<PreparePdf>(
    async (onProgress) => {
      const root = document.querySelector(`[${REPORT_SHARE_ROOT_ATTR}]`);
      if (!root) throw new Error("Open a report tab to share it.");
      const cardsBefore = root.querySelectorAll(`[${V2_CARD_ATTR}]`).length;
      forceMountDeferred();
      const fetched = await waitForData(queryClient);
      // Charts draw in over ~1.5s once their data lands.
      if (fetched || root.querySelectorAll(`[${V2_CARD_ATTR}]`).length !== cardsBefore) await sleep(1600);
      const sub = activeSubTabLabel(root);
      const { summary: lines, preparedBy: by, tabLabel: tab } = latest.current;
      return buildReportPdf({
        title: `Reports · ${tab}${sub ? ` · ${sub}` : ""}`,
        summary: lines,
        preparedBy: by,
        blocks: collectViewBlocks(root),
        onProgress,
      });
    },
    [queryClient],
  );

  if (customOpen || !canShare) return null;
  return <ShareMenu variant="pill" title={`Reports · ${tabLabel}`} prepare={prepare} />;
}
